/**
 * PATH: packages/ui/src/qa/qaPhotoPrefs.tsx
 * FILE_ID: FE-QA-PHOTO-PREFS-001
 * PURPOSE: One store for QA photo size, Fit/Fill, pan and photo selection.
 * LANDMINE: useQaPhotoPrefs always calls the local fallback hook, then picks
 *      context over it. Never make that call conditional — a gallery with no
 *      provider (the QA PWA) must keep its own self-contained chrome.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import { clampSize, usePersistentBool, usePersistentJson, usePersistentSize } from '../components/panelResize'
import {
  QA_PHOTO_DEFAULT_FIT,
  QA_PHOTO_DEFAULT_SIZE,
  QA_PHOTO_LS_FIT,
  QA_PHOTO_LS_PX,
  QA_PHOTO_LS_VIEWS,
  nearestQaPhotoSize,
  resolveQaPhotoView,
  type QaPhotoSize,
  type QaPhotoView,
} from './qaPhotoView'

export interface QaPhotoPrefs {
  viewOf: (id: string) => QaPhotoView
  setSize: (size: QaPhotoSize) => void
  setFit: (fit: boolean) => void
  setPan: (id: string, panX: number, panY: number) => void
  resetOverrides: () => void
  hasOverrides: boolean
  activeView: QaPhotoView
  selectEnabled: boolean
  setSelectEnabled: (next: boolean) => void
  isSelected: (id: string) => boolean
  toggleSelected: (id: string) => void
  selectedCount: number
  clearSelected: () => void
}

const QaPhotoPrefsContext = createContext<QaPhotoPrefs | null>(null)

function useQaPhotoPrefsState(): QaPhotoPrefs {
  const [defaultPx, setDefaultPx] = usePersistentSize(QA_PHOTO_LS_PX, QA_PHOTO_DEFAULT_SIZE)
  const [defaultFit, setDefaultFit] = usePersistentBool(QA_PHOTO_LS_FIT, QA_PHOTO_DEFAULT_FIT)
  const [overrides, setOverrides] = usePersistentJson<Record<string, Partial<QaPhotoView>>>(
    QA_PHOTO_LS_VIEWS,
    {},
  )
  const [selectEnabled, setSelectEnabledRaw] = useState(false)
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())
  const defaultSize = nearestQaPhotoSize(clampSize(defaultPx, 72, 128))

  const viewOf = useCallback(
    (id: string) => resolveQaPhotoView(id, { defaultSize, defaultFit, overrides }),
    [defaultFit, defaultSize, overrides],
  )

  const setSize = useCallback((size: QaPhotoSize) => {
    if (selected.size > 0) {
      const next = { ...overrides }
      for (const id of selected) next[id] = { ...next[id], size }
      setOverrides(next)
      return
    }
    setDefaultPx(size)
  }, [overrides, selected, setDefaultPx, setOverrides])

  const setFit = useCallback((fit: boolean) => {
    if (selected.size > 0) {
      const next = { ...overrides }
      for (const id of selected) next[id] = { ...next[id], fit }
      setOverrides(next)
      return
    }
    setDefaultFit(fit)
  }, [overrides, selected, setDefaultFit, setOverrides])

  const setPan = useCallback((id: string, panX: number, panY: number) => {
    setOverrides({ ...overrides, [id]: { ...overrides[id], panX, panY } })
  }, [overrides, setOverrides])

  const resetOverrides = useCallback(() => setOverrides({}), [setOverrides])

  const setSelectEnabled = useCallback((next: boolean) => {
    setSelectEnabledRaw(next)
    if (!next) setSelected(new Set())
  }, [])

  const isSelected = useCallback((id: string) => selected.has(id), [selected])

  const toggleSelected = useCallback((id: string) => {
    setSelected((ids) => {
      const copy = new Set(ids)
      if (copy.has(id)) copy.delete(id)
      else copy.add(id)
      return copy
    })
  }, [])

  const clearSelected = useCallback(() => setSelected(new Set()), [])

  return useMemo(() => {
    const firstSelected = selected.size > 0 ? [...selected][0] : null
    const activeView = firstSelected
      ? viewOf(firstSelected)
      : { size: defaultSize, fit: defaultFit, panX: 50, panY: 50 }
    return {
      viewOf,
      setSize,
      setFit,
      setPan,
      resetOverrides,
      hasOverrides: Object.keys(overrides).length > 0,
      activeView,
      selectEnabled,
      setSelectEnabled,
      isSelected,
      toggleSelected,
      selectedCount: selected.size,
      clearSelected,
    }
  }, [
    clearSelected,
    defaultFit,
    defaultSize,
    isSelected,
    overrides,
    resetOverrides,
    selectEnabled,
    selected,
    setFit,
    setPan,
    setSelectEnabled,
    setSize,
    toggleSelected,
    viewOf,
  ])
}

export function QaPhotoPrefsProvider({ children }: { children: ReactNode }) {
  const value = useQaPhotoPrefsState()
  return (
    <QaPhotoPrefsContext.Provider value={value}>
      {children}
    </QaPhotoPrefsContext.Provider>
  )
}

export function useQaPhotoPrefsHosted(): boolean {
  return useContext(QaPhotoPrefsContext) != null
}

export function useQaPhotoPrefs(): QaPhotoPrefs {
  const local = useQaPhotoPrefsState()
  const hosted = useContext(QaPhotoPrefsContext)
  return hosted ?? local
}
